// Onboarding: welcome, then three steps, then sign-in (spec section 4).
// Ported screen by screen from the glasshouse reference; the roster and
// suggestions come from the API instead of data.js.
import { useEffect, useRef, useState } from "react";
import { type LegalDocId } from "../../shared/legal.js";
import { TOPICS, type TopicDef } from "../../shared/ontology.js";
import type { ExpertLite } from "../state/api.js";
import { docPath, useStore, type AppStore } from "../state/store.js";
import type { Descriptor } from "../shell/descriptor.js";
import { Avatar } from "../shell/Avatar.js";
import { roleLine } from "../shell/fragments.js";
import { HeroMosaic } from "../shell/HeroMosaic.js";
import { recommendSort } from "../state/recommend.js";

import { AREAS, areaCats } from "../../shared/areas.js";

/* The roster is the last thing on the page, so it scrolls last (30 Jul 2026).
   Nested scrollers normally win: put the pointer over the bench and the wheel
   pages through 140 names while the button below it never arrives. This turns
   that around, sending downward scroll to the window until the panel, and so
   the Get started under it, is fully in view, and only then letting the bench
   move. Upward scroll is left alone, and so is the case where the document
   itself cannot go any further, or the wheel would land nowhere. */
function useLastScroller() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const panel = el?.closest("section");
    if (!el || !panel) return;
    const settled = () =>
      panel.getBoundingClientRect().bottom <= window.innerHeight + 1 ||
      window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY <= 0 || settled()) return;
      e.preventDefault();
      window.scrollBy(0, e.deltaY);
    };
    // Touch needs the same redirect, hand-panned: preventing the default on a
    // touchmove drops momentum, but only for the few frames before the panel
    // settles, after which the bench scrolls natively again.
    let last = 0;
    const onTouchStart = (e: TouchEvent) => {
      last = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY ?? 0;
      const dy = last - y;
      last = y;
      if (dy <= 0 || settled()) return;
      e.preventDefault();
      window.scrollBy(0, dy);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, []);
  return ref;
}

/* The front-door roster: alphabetical, entered at a random point and
   wrapping around (28 Jul 2026), so every load leads with different faces
   instead of always the same first letters. Component state keeps the
   starting point stable across re-renders of one visit; a fresh visit
   remounts and rolls again. No overline since 30 Jul 2026: the panel's own
   heading says what this is, and "On Practice today" above it was the same
   thing said twice. */
function WelcomeRoster() {
  const experts = useStore((s) => s.experts) ?? [];
  const openSheet = useStore((s) => s.openSheet);
  const [entry] = useState(() => Math.random());
  const scroller = useLastScroller();
  const sorted = [...experts].sort((a, b) => a.name.localeCompare(b.name));
  const start = sorted.length ? Math.floor(entry * sorted.length) : 0;
  const rotated = [...sorted.slice(start), ...sorted.slice(0, start)];
  return (
    <div className="obside rostercard">
      <div className="wscroll" ref={scroller}>
        <div className="wgrid">
          {rotated.map((e) => (
            <button
              key={e.id}
              className="wtile"
              aria-label={`More about ${e.name}`}
              onClick={() => openSheet({ kind: "expert-intro", id: e.id })}
            >
              <Avatar e={e} size="l" />
              <strong>{e.name}</strong>
              <small>{e.cat}</small>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* The lockup and the theme control moved out of the cards on 2 Aug 2026
   (Rob), onto the background above them, which is what the public question
   pages already do. They were inside every panel because the wizard hides
   the sidebar and the brand had to live somewhere; sitting in the card it
   read as part of the step, and the first card on the front door carried a
   logo, a tagline, a slideshow and a button all in one box. Outside, it is
   page chrome: the same mark in the same place on all five screens, and the
   card starts at its own first line. The bar is `SoloBar` in the shell,
   rendered for any solo screen; `soloRight` slots what a screen wants beside
   the toggle, like the front door's sign-in. */

function ObTop({ back, step }: { back: string; step: string }) {
  const nav = useStore((s) => s.nav);
  return (
    <div className="obtop">
      <button className="backlink" onClick={() => nav(back)}>
        ‹ Back
      </button>
      <span className="u">{step}</span>
    </div>
  );
}

/* The front door is the website's home page (28 Jul 2026): a hero card
   carrying the daily image and the tagline, then a second card where the
   visitor actually gets started, with the roster browsable beside it. */
export function welcome(state: AppStore): Descriptor {
  const { nav } = state;
  return {
    solo: true,
    hideHead: true,
    nav: null,
    // Returning users get a door in the top right of the brand bar, beside
    // the theme control.
    soloRight: (
      <button className="btn" onClick={() => nav("signin")}>
        Sign in
      </button>
    ),
    main: (
      <>
        <section className="panel glass front-panel" style={{ padding: "26px 26px 24px" }}>
          {/* The tagline leads and the practice under it is the proof (30 Jul
              2026). Setting it inside the brand row to save the height was
              tried and cut the same hour: a display line wedged between the
              lockup and the buttons reads as a toolbar label, not a heading.
              The three explanatory blocks that used to follow, the numbered
              steps, the counts and the four promises, were all cut the same
              day: a front door that has to argue its case in three panels is
              not a front door, and the slide makes the argument by being the
              thing itself. The daily landscape stays as the page behind
              everything; putting it in the card too was the same picture
              inside itself. */}
          <h1 className="hero-h front-h">Practice what they preach.</h1>
          <HeroMosaic />
          <div className="frontcta">
            <button className="btn accent lg" onClick={() => nav("onb-areas")}>
              Get started
            </button>
            <p className="freenote">Free to sign up and use. We do not ask for a card.</p>
          </div>
        </section>
        <section className="panel glass front-panel" style={{ padding: "26px 26px 24px" }}>
          {/* The plain sentence heads the bench (30 Jul 2026): it is the line
              that says what we do with what these people publish, so it sits
              on the panel that names them. */}
          <p className="front-lead">
            Follow those you trust. Turn what they publish into a daily practice.
          </p>
          {/* The no-affiliation line came off here on 2 Aug 2026 (Rob): the
              footer's standing disclosure is on this page too and already
              says experts are not affiliated with or endorsing
              Practice, so the panel was carrying the same sentence twice
              within one screen. It stays on the expert profiles, which is
              the surface built around one real person's name and face.
              (Counsel's launch note 11 asked for it on the wall of faces
              specifically; the footer covers the page, the flat wording
              covers the profile.) */}
          {/* The whole bench, browsable before signing up. It had a column of
              promises beside it until 30 Jul 2026; on its own it runs the
              full width. */}
          <div className="rosterwrap">
            <WelcomeRoster />
          </div>
          {/* The way in sits at the end of the page, after the names (30 Jul
              2026): someone who has just read the whole bench has scrolled
              well past the first button, and the returning-user door belongs
              where the reading stops. */}
          <div className="frontcta rostercta">
            <p className="freenote centred">
              Already practicing?{" "}
              <button className="credlink" onClick={() => nav("signin")}>
                Sign in
              </button>
            </p>
            <button className="btn accent lg" onClick={() => nav("onb-areas")}>
              Get started
            </button>
            <p className="freenote">Free to sign up and use. We do not ask for a card.</p>
          </div>
        </section>
      </>
    ),
  };
}

export function onbAreas(state: AppStore): Descriptor {
  const { nav, toggleArea, onboarding } = state;
  const n = Object.values(onboarding.areas).filter(Boolean).length;
  return {
    solo: true,
    hideHead: true,
    nav: null,
    main: (
      <section className="panel glass" style={{ padding: 26 }}>
        <ObTop back="welcome" step="Step 1 of 3" />
        <h2 className="obh">What do you want to work on?</h2>
        <p className="obp">
          Pick as many as you like. This decides who you see first and what reaches your day, and you can change it any time.
        </p>
        <div className="obgrid">
          {AREAS.map(([id, label, emoji]) => (
            <button
              key={id}
              className="goal"
              aria-pressed={Boolean(onboarding.areas[id])}
              onClick={() => toggleArea(id)}
            >
              <span className="emo">{emoji}</span>
              <span>{label}</span>
              <span className="ck">✓</span>
            </button>
          ))}
        </div>
        {/* Ordinary block flow below the grid: the disclaimer is one line,
            the button the next, full width, nothing shared. The way out sits
            directly under the grid and above the advanced panel (1 Aug 2026,
            Rob), so the ordinary path is grid, Continue, done, and the panel
            is something you go looking for rather than something you have to
            get past. */}
        <p className="obfoot" style={{ marginTop: 28 }}>
          We do not ask for diagnoses, symptoms or medications, and we never will.
        </p>
        <button
          className="btn solid lg obcontinue"
          style={{ marginTop: 22 }}
          disabled={n === 0}
          onClick={() => nav("onb-experts")}
        >
          {n ? "Continue" : "Pick at least one"}
        </button>
        {/* Not until they have picked something (1 Aug 2026, Rob). The panel
            describes what their areas switched on, so with nothing picked it
            is a row of switches with no reason to exist yet. */}
        {n > 0 && <AdvancedTopics />}
      </section>
    ),
  };
}

/* One topic switch row. The stated reason sits behind a help control rather
   than under every row; disclosure, not removal (spec section 4). The
   control toggles its text in place so opening one does not repaint the
   screen (spec section 9, exception 4). */
export function TopicRow({ t, checked, onToggle, muteLabel }: {
  t: TopicDef;
  checked: boolean;
  onToggle: () => void;
  muteLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="swrow">
      <span className="grow">
        <span className="swhead">
          <strong>{t.label}</strong>
          <button
            className="helpq"
            aria-expanded={open}
            aria-label={`Why ${t.label} is set this way`}
            onClick={() => setOpen(!open)}
          >
            ?
          </button>
        </span>
        {open && <p className="whytext">{t.reason}</p>}
      </span>
      <button
        className="sw"
        role="switch"
        aria-checked={checked}
        aria-label={muteLabel ?? t.label}
        onClick={onToggle}
      />
    </div>
  );
}

/* The topic filters, folded under step 1 as a disclosure (1 Aug 2026, Rob).
   They were their own step and it was a wall: eleven switches about fasting,
   detox and vaccines, put to someone who did not yet know what the site
   does. The areas above now seed them (AREA_TOPICS in shared/areas.ts), so a
   reader who never opens this still ends up with a coherent set, and opening
   it is for changing our answer rather than supplying one.

   Collapsed by default and it stays that way while the panel is open: this
   is not a step, so nothing here gates Continue. */
function AdvancedTopics() {
  const onboarding = useStore((s) => s.onboarding);
  const toggleObTopic = useStore((s) => s.toggleObTopic);
  const nav = useStore((s) => s.nav);
  const [open, setOpen] = useState(false);
  const rows = TOPICS.filter((t) => t.filterable);
  const on = rows.filter((t) => onboarding.topics[t.id]).length;
  const off = rows.length - on;
  return (
    <div className="obadv">
      <button className="obadvhead" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="cv" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="grow">
          <strong>Advanced topic filters</strong>
          <small>
            Some kinds of advice stay out of your day unless you ask for them. {on} of {rows.length} are on, from the areas you picked.
          </small>
        </span>
      </button>
      {open && (
        <div className="obadvbody">
          {/* The two columns the old step 2 had: the switches, and the card
              that explains why any of them start off. It came back with the
              panel (1 Aug 2026, Rob); the switches alone left the reader to
              infer our reasoning from eleven labels. */}
          <div className="obcols">
            <section>
              {rows.map((t) => (
                <TopicRow
                  key={t.id}
                  t={t}
                  checked={Boolean(onboarding.topics[t.id])}
                  onToggle={() => toggleObTopic(t.id)}
                />
              ))}
              {/* A second way out, at the foot of the eleven rows (1 Aug
                  2026, Rob), so reading to the bottom of the panel does not
                  mean scrolling back past it. In the rows' own column, so it
                  shares their exact width. Always enabled: the panel does
                  not appear until an area is picked. */}
              <button
                className="btn solid lg"
                style={{ marginTop: 16 }}
                onClick={() => nav("onb-experts")}
              >
                Continue
              </button>
            </section>
            {/* Rewritten 1 Aug 2026 (Rob: confusing and overly complicated).
                It had three blocks arguing about consent, quality and what
                the next step would do with the answers. A reader looking at
                eleven switches needs three facts: why these topics and not
                others, what we already did for them, and what "off" costs
                them. One sentence each. */}
            <div className="obside">
              <span className="u">Why some start off</span>
              <p style={{ marginTop: 6 }}>
                These are the topics people most often want to decide for themselves. We switch on the ones your areas cover and leave the rest off. When a topic is off, related content stays hidden. You can always change these later.
              </p>
              <div className="notice safe" style={{ marginTop: 12 }}>
                <span className="ni">◇</span>
                <span>
                  <b>Nothing is blocked or deleted.</b>{" "}
                  {off
                    ? `${off} ${off > 1 ? "are" : "is"} off right now. If a filter ever hides something, we say so and link back here.`
                    : "Everything is on. If a filter ever hides something, we say so and link back here."}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* The people list with its reveal state (revised 28 Jul 2026): the whole
   roster ranked by recommendation (areas and switches, both from step 1),
   the first EIGHT shown, and "See more" appends into the SAME grid,
   eight at a time (nine when the count shown is odd, so the two columns
   stay level), until the roster runs out. */
const FIRST_SHOWN = 8;

function ExpertPicker() {
  const state = useStore();
  const { onboarding, toggleObFollow, openSheet } = state;
  const experts = state.experts ?? [];
  const cats = new Set(areaCats(onboarding.areas));
  const scored = recommendSort(experts, cats, (id) => Boolean(onboarding.topics[id]));
  // Anyone the reader arrived already following, which today means the person
  // whose page they pressed Get started on, leads the list: the choice they
  // have already made should not be something they have to find again behind
  // See more. Captured once at mount rather than read live, or rows would
  // reorder under the pointer as people are followed on this screen.
  const [arrived] = useState(() => new Set(onboarding.follows));
  const ranked = arrived.size
    ? [...scored.filter((e) => arrived.has(e.id)), ...scored.filter((e) => !arrived.has(e.id))]
    : scored;
  const [extra, setExtra] = useState(0);
  // The initial batch fills the panel's real height: measured after render
  // (rows per screen times columns), never fewer than eight. Resizing
  // refills; See more still reveals from there.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [base, setBase] = useState(FIRST_SHOWN);
  useEffect(() => {
    const fit = () => {
      const el = scrollRef.current;
      if (!el) return;
      const rowH = (el.querySelector(".frow") as HTMLElement | null)?.offsetHeight || 60;
      // jsdom has no matchMedia; the floor of eight carries the tests.
      const narrow = typeof window.matchMedia === "function" && window.matchMedia("(max-width:820px)").matches;
      setBase(Math.max(FIRST_SHOWN, Math.ceil(el.clientHeight / rowH) * (narrow ? 1 : 2)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  // Each See more lands you on the fresh rows: once they exist, the list
  // glides to its bottom, where the next See more also is.
  useEffect(() => {
    if (extra === 0) return;
    const el = scrollRef.current;
    el?.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [extra]);
  const shown = ranked.slice(0, base + extra);
  const remaining = ranked.length - shown.length;

  const row = (e: ExpertLite) => {
    const on = onboarding.follows.includes(e.id);
    const openPeek = () => openSheet({ kind: "expert-peek", id: e.id });
    return (
      <div
        key={e.id}
        className="lrow frow"
        role="link"
        tabIndex={0}
        onClick={openPeek}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            openPeek();
          }
        }}
      >
        <span>
          <Avatar e={e} size="m" />
        </span>
        <span className="grow">
          <span className="frowtop">
            <strong style={{ fontFamily: "var(--ui)", fontSize: 14 }}>{e.name}</strong>
            {e.cat && <span className="cattx">{e.cat}</span>}
          </span>
          <small>{roleLine(e)}</small>
        </span>
        <button
          className={`btn${on ? " following" : ""}`}
          onClick={(ev) => {
            ev.stopPropagation();
            toggleObFollow(e.id);
          }}
        >
          {on ? "Following" : "Follow"}
        </button>
      </div>
    );
  };

  return (
    <div className="followscroll" ref={scrollRef}>
      {shown.length > 0 && <div className="frows">{shown.map(row)}</div>}
      {remaining > 0 && (
        <button
          className="btn"
          style={{ marginTop: 12 }}
          onClick={() => setExtra(extra + (shown.length % 2 === 1 ? 9 : 8))}
        >
          See more ({remaining})
        </button>
      )}
    </div>
  );
}

/* The step's foot: the button leads on the left half, the advice fills the
   right, and the counterweight nudge waits until they have someone to
   counterweigh.

   Only the button has to be in the viewport (2 Aug 2026, Rob). The whole card
   was inside the height the step reserves, so on a phone, where the advice
   stacks under the button, a sentence nobody needs before pressing was
   costing the list a row and a half of people. Whatever sits below the button
   is measured and handed to `.obstep` as `--ctaspill`, which adds it to the
   step's height: the page grows by exactly that much, the button's bottom
   edge lands on the viewport bottom, and the list takes the freed height.
   The advice is a short scroll below the fold.

   Measured, not assumed from a breakpoint (the reading `useFitRow` already
   does for the hero byline): a long line stacks this card at widths no media
   query names, and side by side there is nothing below the button, so the
   spill is zero and the desktop layout never moves. */
function FollowCta({ n, onContinue }: { n: number; onContinue: () => void }) {
  const card = useRef<HTMLElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const advice = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const fit = () => {
      const c = card.current;
      const b = btn.current;
      const t = advice.current;
      const step = c?.closest<HTMLElement>(".obstep");
      if (!c || !b || !t || !step) return;
      const cr = c.getBoundingClientRect();
      const br = b.getBoundingClientRect();
      const stacked = t.getBoundingClientRect().top >= br.bottom - 1;
      // Everything below the button: the row gap, the advice, and the card's
      // own bottom padding. Setting this cannot change it, because the card
      // and the button move down together, so there is no loop to settle.
      const spill = stacked ? Math.max(0, Math.round(cr.bottom - br.bottom)) : 0;
      step.style.setProperty("--ctaspill", `${spill}px`);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  });
  return (
    <section className="obcta glass" ref={card}>
      <button className="btn solid" ref={btn} disabled={n < 1} onClick={onContinue}>
        {n >= 1 ? "Build my first week" : "Follow someone to continue"}
      </button>
      <span className="obctatext" ref={advice}>
        {n >= 1
          ? "You might also follow someone you expect to be wrong. When they disagree, we'll show you where."
          : "Follow at least one person to start. Add more whenever you like."}
      </span>
    </section>
  );
}

/* Step 2: the people, suggested from step 1's areas. Two-line rows, not
   cards (spec section 4): name plus tags, then the credentialed title.
   Following one person is enough to start. */
export function onbExperts(state: AppStore): Descriptor {
  const { nav, onboarding } = state;
  const n = onboarding.follows.length;

  return {
    solo: true,
    hideHead: true,
    nav: null,
    main: (
      <div className="obstep">
        {/* One viewport-height flex column: this panel flexes and its rows
            scroll inside; the CTA card below is rigid. Two separate cards
            that can never overlap (28 Jul 2026). */}
        <section className="panel glass obfollow" style={{ padding: 26 }}>
          <ObTop back="onb-areas" step="Step 2 of 3" />
          <h2 className="obh">Follow a few sources</h2>
          <p className="obp">
            You’ll see content from those you follow.
          </p>
          <ExpertPicker />
        </section>
        <FollowCta n={n} onContinue={() => nav("onb-email")} />
      </div>
    ),
  };
}

/* One consent checkbox. Unchecked on arrival and never pre-ticked: counsel's
   flow requires an affirmative act for each, and a box we ticked for them is
   not an act (1 Aug 2026). The document link sits inside the sentence and is
   styled to be obviously a link, not muted. */
function ConsentRow({ id, checked, onToggle, children }: {
  id: string;
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <label className="consent" htmlFor={id}>
      <input id={id} type="checkbox" checked={checked} onChange={onToggle} />
      <span>{children}</span>
    </label>
  );
}

/* Email capture, after the practice is built rather than before it. No
   password, ever, and nothing on this screen may promise mail we do not
   send.

   Counsel's signup flow lands here (1 Aug 2026, spec section 4): an 18-plus
   and Terms acceptance, a SEPARATE health-data consent that must not be
   bundled into it, the product disclosure above the button, and a button
   that names what it does. The sign-in email follows the consent event
   rather than being the consent event, because "Send my link" is weak
   evidence of assent to an arbitration clause. */
function EmailStep() {
  const finishOnboarding = useStore((s) => s.finishOnboarding);
  const linkSent = useStore((s) => s.linkSent);
  const openSheet = useStore((s) => s.openSheet);
  const [email, setEmail] = useState("");
  const [invite, setInvite] = useState("");
  const [needsInvite, setNeedsInvite] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeHealth, setAgreeHealth] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  /* A real anchor, not a button (2 Aug 2026): every document has an address
     now, so the link can be copied or opened in a new tab like any other, and
     an anchor is inline for real, which is what keeps the full stop after it
     off a line of its own. The click still opens the modal, because a reader
     checking what they are agreeing to should not lose the address they have
     half typed. */
  const doc = (id: LegalDocId, label: string) => (
    <a
      className="credlink"
      href={docPath(id)}
      onClick={(e) => {
        e.preventDefault();
        openSheet({ kind: "legal", doc: id });
      }}
    >
      {label}
    </a>
  );

  if (linkSent) {
    return (
      <div className="notice safe" style={{ marginTop: 6 }}>
        <span className="ni">✉</span>
        <span>
          <b>Your link is on its way to {linkSent}.</b> Open it on this device and your first week is waiting. The link works once and expires in 15 minutes; nothing is lost if it does, just ask for another.
        </span>
      </div>
    );
  }

  return (
    <div>
      <div className="field">
        <span className="u">Email</span>
        {/* Spec section 9, exception 2: the field must never be replaced
            mid-typing. React keeps the element; state updates patch value,
            hint, and button in place. */}
        <input
          className="tin"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-label="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {email && !looksLikeEmail && (
          <p className="obfoot" style={{ color: "var(--clay)" }}>
            That does not look like an email address yet.
          </p>
        )}
      </div>
      {/* No invite field by default (1 Aug 2026, Rob). The gate is off
          unless PRACTICE_INVITE_CODE is set, and asking every visitor for a
          code that nothing is checking is a locked door with no lock. If the
          gate is ever switched back on, the first refusal carries
          `needsInvite` and the field appears here with the typed address
          still in place, so turning it on needs no deploy on this side. */}
      {needsInvite && (
        <div className="field">
          <span className="u">Invite code</span>
          <input
            className="tin"
            type="text"
            autoComplete="off"
            placeholder="From your invitation"
            aria-label="Your invite code"
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
          />
        </div>
      )}
      {error && (
        <p className="obfoot" style={{ color: "var(--rose)" }}>
          {error}
        </p>
      )}
      <div className="consents">
        <ConsentRow id="c-terms" checked={agreeTerms} onToggle={() => setAgreeTerms(!agreeTerms)}>
          I am 18 or older and agree to the {doc("terms", "Terms of Use")}. I acknowledge the{" "}
          {doc("privacy", "Privacy Policy")}.
        </ConsentRow>
        <ConsentRow id="c-health" checked={agreeHealth} onToggle={() => setAgreeHealth(!agreeHealth)}>
          I consent to Lab 10 collecting and using the health areas, topics, experts, practices, choices, and activity I provide to personalize and operate Practice, as described in the{" "}
          {doc("health", "Consumer Health Data Privacy Policy")}. I can withdraw this consent and delete my data at any time. Practice cannot create a personalized account without this data.
        </ConsentRow>
      </div>
      {/* The product disclosure came off this screen on 2 Aug 2026 (Rob).
          Counsel's step C asked for it beside the button rather than in a
          footnote, and the footer carries it on this page in stronger words:
          "not medical advice, diagnosis, or treatment" against the shorter
          "not medical care" that stood here. Two of the same paragraph a
          screen apart is not twice the disclosure. */}
      <div className="btnrow" style={{ marginTop: 12 }}>
        <button
          className="btn solid lg"
          // The server enforces the invite gate when it is on (and exempts
          // existing accounts), so an empty code never blocks the button.
          // Both consents do block it: that is what makes them consents.
          disabled={!looksLikeEmail || !agreeTerms || !agreeHealth || sending}
          onClick={() => {
            setSending(true);
            setError(null);
            void finishOnboarding(email, invite).then((r) => {
              setSending(false);
              if (r.needsInvite) setNeedsInvite(true);
              if (!r.ok) setError(r.error ?? "Something went wrong sending your link. Try again.");
            });
          }}
        >
          {sending ? "Creating your account…" : "Create my Practice account"}
        </button>
      </div>
    </div>
  );
}

/* TermsLine is gone (1 Aug 2026). Acceptance used to ride the signup action
   itself, one sentence of muted small print under the button saying that
   asking for a link was the agreement. Counsel's advice is that a
   sign-in-wrap of that kind is weak evidence of assent, and far too weak for
   an arbitration clause, so acceptance is now two unchecked boxes that gate
   the button (see EmailStep). The sentence also carried "Practice is just an
   engine", which counsel told us to stop saying: we select sources, rewrite
   and label them, infer positions, personalize output, and turn claims into
   instructions, and none of that is what an engine does. */

export function onbEmail(): Descriptor {
  return {
    solo: true,
    hideHead: true,
    nav: null,
    main: (
      <section className="panel glass" style={{ padding: 26 }}>
        {/* Numbered rather than "Last thing" (1 Aug 2026, Rob): collecting
            the address is a step, and calling it something else made the
            count say two when the reader had three screens to go. */}
        <ObTop back="onb-experts" step="Step 3 of 3" />
        <h2 className="obh">What email do you want to use to sign in?</h2>
        <p className="obp">
          Your email is your login. We'll send you a magic link and you're in.
        </p>
        <div className="obcols" style={{ marginTop: 6 }}>
          <EmailStep />
          <div className="obside">
            <span className="u">What happens next</span>
            <p style={{ marginTop: 6 }}>
              Confirming your email matters: it is what lets you sign in on a second device, and what lets us give your practice back to you if you lose access to this one.
            </p>
            <div className="notice safe" style={{ marginTop: 14 }}>
              <span className="ni">◇</span>
              <span>
                <b>One address, one purpose.</b> Signing you in. We do not send a newsletter, a digest, or a weekly summary, and we never share your email with anyone else.
              </span>
            </div>
            {/* Full width of the card (1 Aug 2026, Rob): `.freenote` caps
                itself at 46ch for the front door's centred column, which
                inside this card just made the last line short of the edge
                everything above it reaches. */}
            <p className="freenote" style={{ marginTop: 12, maxWidth: "none" }}>
              Signing up is free and we are not asking for a card. If we ever charge for something, it will be a new thing you choose, not this.
            </p>
          </div>
        </div>
      </section>
    ),
  };
}

/* Returning users and expired links land here; the shell shows the reason
   rather than a dead end. */
function SignInPanel() {
  const requestSignIn = useStore((s) => s.requestSignIn);
  const linkSent = useStore((s) => s.linkSent);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const expired = new URLSearchParams(location.search).get("link") === "expired";

  return (
    <div style={{ maxWidth: 480 }}>
      {expired && !linkSent && (
        <div className="notice guard" style={{ marginBottom: 14 }}>
          <span className="ni">◷</span>
          <span>
            <b>That link has expired or was already used.</b> Links work once and last 15 minutes. Enter your address and we will send a fresh one.
          </span>
        </div>
      )}
      {linkSent ? (
        <div className="notice safe">
          <span className="ni">✉</span>
          <span>
            <b>Your link is on its way to {linkSent}.</b> Open it on this device to sign in. It works once and expires in 15 minutes.
          </span>
        </div>
      ) : (
        <div>
          <div className="field">
            <span className="u">Email</span>
            <input
              className="tin"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              aria-label="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && (
              <p className="obfoot" style={{ color: "var(--rose)" }}>
                {error}
              </p>
            )}
          </div>
          <div className="btnrow" style={{ marginTop: 14 }}>
            <button
              className="btn solid lg"
              disabled={!looksLikeEmail || sending}
              onClick={() => {
                setSending(true);
                setError(null);
                void requestSignIn(email).then((r) => {
                  setSending(false);
                  if (!r.ok) setError(r.error ?? "Something went wrong sending your link. Try again.");
                });
              }}
            >
              {sending ? "Sending your link…" : "Send my sign-in link"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function signin(state: AppStore): Descriptor {
  const { nav } = state;
  return {
    solo: true,
    hideHead: true,
    nav: null,
    main: (
      <section className="panel glass" style={{ padding: 26 }}>
        <h2 className="obh">Sign in</h2>
        <p className="obp">
          Your email is your login. We send a link, you tap it, you are in. There is no password.
        </p>
        <SignInPanel />
        <p className="freenote" style={{ marginTop: 16 }}>
          New here?{" "}
          <button className="credlink" onClick={() => nav("welcome")}>
            Start at the beginning
          </button>
        </p>
      </section>
    ),
  };
}
